import { c as _c } from "react/compiler-runtime";
import { dateToIso8601, isValidDate, maskedDobToLocale } from '@onefootprint/core';
import type { Locale } from '@onefootprint/request-types';
import { useEffect, useRef, useState } from 'react';
import { type FieldValues, type Path, type PathValue, type RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { dateMaskForPattern, resolveBrowserDatePattern, toDateFormatPattern } from '../date-mask';
import FormInput, { type FormInputProps } from '../form-input/form-input';
import useDateExampleHint from '../use-date-example-hint';
export type FormDobProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  // An explicitly configured SDK locale sets the mask; otherwise it follows the browser locale.
  locale?: Locale;
  scrubbedDobValue?: string;
} & Omit<FormInputProps<TFieldValues>, 'name' | 'rules' | 'inputMode'>;
export default function FormDob(t0) {
  const $ = _c(50);
  let hint;
  let locale;
  let props;
  let rules;
  let scrubbedDobValue;
  if ($[0] !== t0) {
    ({
      rules,
      locale,
      scrubbedDobValue,
      hint,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = hint;
    $[2] = locale;
    $[3] = props;
    $[4] = rules;
    $[5] = scrubbedDobValue;
  } else {
    hint = $[1];
    locale = $[2];
    props = $[3];
    rules = $[4];
    scrubbedDobValue = $[5];
  }
  const {
    t
  } = useTranslation("ui");
  const {
    setValue
  } = useFormContext();
  let t1;
  if ($[6] !== locale) {
    t1 = toDateFormatPattern(locale) ?? resolveBrowserDatePattern();
    $[6] = locale;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const pattern = t1;
  const mask = dateMaskForPattern[pattern];
  const exampleHint = useDateExampleHint(pattern);
  const isInitialValueScrubbed = !!scrubbedDobValue;
  let t2;
  if ($[8] !== pattern || $[9] !== scrubbedDobValue) {
    t2 = scrubbedDobValue ? maskedDobToLocale(scrubbedDobValue, pattern) : undefined;
    $[8] = pattern;
    $[9] = scrubbedDobValue;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const scrubbedDisplayValue = t2;
  const [showScrubbedValue, setShowScrubbedValue] = useState(isInitialValueScrubbed);
  const previousScrubbedValue = useRef(scrubbedDobValue);
  let t3;
  let t4;
  if ($[11] !== props.name || $[12] !== scrubbedDisplayValue || $[13] !== scrubbedDobValue || $[14] !== setValue) {
    t3 = () => {
      if (scrubbedDobValue && scrubbedDobValue !== previousScrubbedValue.current) {
        setShowScrubbedValue(true);
        setValue(props.name, scrubbedDisplayValue as PathValue<TFieldValues, Path<TFieldValues>>);
      }
      previousScrubbedValue.current = scrubbedDobValue;
    };
    t4 = [scrubbedDobValue, scrubbedDisplayValue, props.name, setValue];
    $[11] = props.name;
    $[12] = scrubbedDisplayValue;
    $[13] = scrubbedDobValue;
    $[14] = setValue;
    $[15] = t3;
    $[16] = t4;
  } else {
    t3 = $[15];
    t4 = $[16];
  }
  useEffect(t3, t4);
  let t5;
  if ($[17] !== isInitialValueScrubbed || $[18] !== pattern || $[19] !== scrubbedDisplayValue || $[20] !== scrubbedDobValue) {
    t5 = value => {
      if (isInitialValueScrubbed && (value === scrubbedDobValue || value === scrubbedDisplayValue)) {
        return;
      }
      if (!value) {
        return value;
      }
      return dateToIso8601(value, pattern) ?? value;
    };
    $[17] = isInitialValueScrubbed;
    $[18] = pattern;
    $[19] = scrubbedDisplayValue;
    $[20] = scrubbedDobValue;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== pattern || $[23] !== t) {
    t6 = value_0 => {
      if (!value_0) {
        return true;
      }
      if (!isValidDate(value_0, pattern)) {
        return t("components.form.dob.errors.invalid");
      }
      return true;
    };
    $[22] = pattern;
    $[23] = t;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== rules || $[26] !== t5 || $[27] !== t6) {
    t7 = {
      setValueAs: t5,
      validate: t6,
      ...rules
    };
    $[25] = rules;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const dobRules = t7;
  let t8;
  if ($[29] !== props.name || $[30] !== setValue || $[31] !== showScrubbedValue) {
    t8 = () => {
      if (showScrubbedValue) {
        setShowScrubbedValue(false);
        setValue(props.name, "" as PathValue<TFieldValues, Path<TFieldValues>>);
      }
    };
    $[29] = props.name;
    $[30] = setValue;
    $[31] = showScrubbedValue;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const handleFocus = t8;
  let t9;
  if ($[33] !== isInitialValueScrubbed || $[34] !== props.name || $[35] !== scrubbedDisplayValue || $[36] !== setValue || $[37] !== showScrubbedValue) {
    t9 = e => {
      if (!e.target.value && !showScrubbedValue && isInitialValueScrubbed) {
        setShowScrubbedValue(true);
        setValue(props.name, scrubbedDisplayValue as PathValue<TFieldValues, Path<TFieldValues>>);
      }
    };
    $[33] = isInitialValueScrubbed;
    $[34] = props.name;
    $[35] = scrubbedDisplayValue;
    $[36] = setValue;
    $[37] = showScrubbedValue;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const handleBlur = t9;
  let t10;
  if ($[39] !== mask || $[40] !== scrubbedDisplayValue || $[41] !== showScrubbedValue) {
    t10 = showScrubbedValue ? {
      value: scrubbedDisplayValue
    } : {
      mask,
      placeholder: mask.placeholder
    };
    $[39] = mask;
    $[40] = scrubbedDisplayValue;
    $[41] = showScrubbedValue;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const scrubbedOverrideProps = t10;
  const t11 = hint ?? exampleHint;
  let t12;
  if ($[43] !== dobRules || $[44] !== handleBlur || $[45] !== handleFocus || $[46] !== props || $[47] !== scrubbedOverrideProps || $[48] !== t11) {
    t12 = <FormInput {...props} hint={t11} rules={dobRules} inputMode="numeric" {...scrubbedOverrideProps} onFocusCapture={handleFocus} onBlur={handleBlur} />;
    $[43] = dobRules;
    $[44] = handleBlur;
    $[45] = handleFocus;
    $[46] = props;
    $[47] = scrubbedOverrideProps;
    $[48] = t11;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  return t12;
}